import { useContext } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { API_HTTP } from "./endpoints";
import ApiInterface from "./api-interface";
import { useSnackbar } from "./useSnackbar";
import { resumeAnalyzerApiEndpoints } from "./endpoints";
import type { User } from "firebase/auth";
import { AuthContext } from "../../context/AuthProvider";
import { GetResumeApiResponse } from "../../DataModels/DataModels";
//import { ResumeProfile } from "../../DataModels/DataModels";

const ONE_WEEK_DURATION_IN_MS = 7 * 24 * 60 * 60 * 1000;

// Avoid using keys with "*" because it may cause incorrect output from detectMIMETypeFromExtension()
const RESUME_MIME_TYPES = {
	".docx":
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	".pdf": "application/pdf",
	".doc": "application/msword",
	".txt": "text/plain",
	".md": "text/markdown",
	".markdown": "text/markdown"
};

const detectMIMETypeFromExtension = (file: File) => {
	const extension = file.name.substring(file.name.lastIndexOf("."));
	const mimeType: string | undefined =
		RESUME_MIME_TYPES[extension as keyof typeof RESUME_MIME_TYPES];

	// Return an empty string if the MIME type is unknown (this shoud never happen)
	return mimeType ?? "";
};

export const useResume = () => {
	const authContext = useContext(AuthContext);
	const user: User | null = authContext?.user as User;
	const userId = user?.uid;
	const { showSnack } = useSnackbar();
	const queryClient = useQueryClient();

	const uploadResume = useMutation<string, any, File, any>({
		mutationFn: async (resume: File) => {
			//console.log("Uploading resume...");
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);

			const formData = new FormData();

			// Fall back to identifying the MIME type from the file extensions if the browser can't detect it
			if (!resume.type) {
				resume = {
					...resume,
					type: detectMIMETypeFromExtension(resume)
				};
			}

			formData.append("resume", resume);
			//console.log(`Posting to server`);
			const response = await apiClient.post(
				resumeAnalyzerApiEndpoints.resumeUpload,
				formData,
				{},
				{
					timeout: 0 // Disable axios timeout
				}
			);

			// const rVal: ResumeProfile = {
			// 	//...response.resumeProfileInfo,
			// 	resumeMarkDown: response.resumeMarkdown
			// };

			return response?.resumeMarkdown;
		},
		onSuccess: (resumeData) => {
			// Directly update the react-query cache here to refresh the resume visualization area
			// See https://tkdodo.eu/blog/mastering-mutations-in-react-query#direct-updates
			queryClient.setQueryData(["user", userId, "resume"], resumeData);
			//console.log(`Recorded resume in cache`);

			return resumeData;
		},
		onError: (error: any) => {
			console.log("useResume error:", error);
			showSnack("NotifyNetworkError", "light");
		}
	});

	const resumeQuery = useQuery<string>({
		queryKey: ["user", user?.uid, "resume"],
		queryFn: async (): Promise<string> => {
			if (!user?.uid) throw new Error("User not authenticated");
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);
			const res: GetResumeApiResponse = await apiClient.get(
				resumeAnalyzerApiEndpoints.resume
			);
			//console.log(res?.resume);
			return res.resume ?? "";
		},
		enabled: !!userId,
		staleTime: ONE_WEEK_DURATION_IN_MS
	});

	//console.log(resumeQuery.data);
	return {
		uploadResume,
		resume: resumeQuery.data,
		isLoading: resumeQuery.isLoading
	};
};
